import { ComponentProps, createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { Dialog } from '@/components/core/dialog';
import { useOptionallyControlledState } from '@/shared/hooks/useOptionallyControlledState';

type DialogContextValue = {
  isOpen: boolean;
  setIsOpen: (value: boolean | ((previous: boolean) => boolean)) => void;
};
const DialogContext = createContext<DialogContextValue>({
  isOpen: false,
  setIsOpen: () => {
    throw new Error('DialogContext not initialized');
  },
});

export const useDialogContext = () => useContext(DialogContext);

/**
 * Only lets the given value change when the dialog is open.
 * Use this inside DialogContent components with props that change when the dialog opens/closes, e.g. the DTO being
 * edited, to avoid content flickering when closing the dialog.
 */
export function useWhileDialogOpen<T>(value: T): T {
  const { isOpen } = useDialogContext();
  const cacheRef = useRef(value);
  useEffect(() => {
    if (isOpen) {
      cacheRef.current = value;
    }
  }, [isOpen, value]);
  return isOpen ? value : cacheRef.current;
}

/**
 * This is a utility component that manages the open state of a Dialog and provides it via context.
 * Use this in conjunction with useDialogContext and useWhileDialogOpen.
 */
export function DialogWithContext({
  open,
  onOpenChange,
  defaultOpen = false,
  ...props
}: ComponentProps<typeof Dialog>) {
  const [isOpen, setIsOpen] = useOptionallyControlledState({
    default: defaultOpen,
    controlled: open,
    setControlled: onOpenChange,
  });
  const contextValue = useMemo(() => ({ isOpen, setIsOpen }), [isOpen, setIsOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} {...props}>
      <DialogContext.Provider value={contextValue}>{props.children}</DialogContext.Provider>
    </Dialog>
  );
}
